import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";
import { anthropicMessagesUsage } from "./usage";

const DEFAULT_ANTHROPIC_BASE_URL = "https://api.anthropic.com/v1";

const anthropicModelsFetch = openAiModelsFetch(
  "Fetch models from Anthropic",
  // The default page size is 20 and extract reads a single response, so
  // anything past the first page would be dropped.
  ({ baseUrl }) =>
    baseUrlEndpoint(baseUrl, DEFAULT_ANTHROPIC_BASE_URL, "/models?limit=100")
);

export const anthropic = defineConnector({
  identifier: "anthropic",
  name: "Anthropic",
  tagLine: "AI models and Claude",
  keywords: "claude opus sonnet haiku llm models inference messages",
  logo,
  protocol: "http",
  tags: ["featured", "ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_ANTHROPIC_BASE_URL),
  auth: (app) =>
    headerAuth([
      {
        name: "anthropic-version",
        value: "2023-06-01",
      },
      { name: "x-api-key", value: app.secret.apiKey },
    ]),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Anthropic Console](https://console.anthropic.com/settings/keys) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "Anthropic API key",
          placeholder: "sk-ant-...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_ANTHROPIC_BASE_URL,
          defaultValue: DEFAULT_ANTHROPIC_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default Anthropic API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "claude-opus-4-8" }],
          addLabel: "Add model",
          fetch: anthropicModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_ANTHROPIC_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    ANTHROPIC_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_ANTHROPIC_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.ANTHROPIC_BASE_URL as the Anthropic API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: anthropicMessagesUsage(),
});
